import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * Shuffled Frog Leaping Algorithm (SFLA) implementation from scratch
 * Based on memeplex structure and local/global search
 */

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  // Distribute frogs (chromosomes) round-robin style
  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  let idx = 0;
  for (const memeplex of memeplexes) {
    for (const frog of memeplex) {
      population[idx++] = frog;
    }
  }
}

/**
 * Updates worst frog in memeplex using local best
 */
function updateWorstFrog(
  worst: Chromosome,
  best: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][]
): Chromosome {
  const step = Math.floor(Math.random() * taskCount);
  const newFrog: Chromosome = {
    x: [...worst.x],
    fitness: 0,
    rwp: 0,
  };

  // Move towards best frog
  for (let i = 0; i < step; i++) {
    const taskIdx = Math.floor(Math.random() * taskCount);
    newFrog.x[taskIdx] = best.x[taskIdx];
  }

  calculateChromosomeFitness(newFrog, matrix, taskCount, serviceCount);
  return newFrog;
}

/**
 * SFLA evolution for one generation
 */
function evolveSFLA(
  population: Chromosome[],
  memeplexes: Chromosome[][],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  globalBest: Chromosome
): void {
  for (const memeplex of memeplexes) {
    // Sort memeplex by fitness (best to worst)
    memeplex.sort((a, b) => b.fitness - a.fitness);
    const memeplexBest = memeplex[0];
    const memeplexWorst = memeplex[memeplex.length - 1];

    // Try to improve worst frog using memeplex best
    let improved = updateWorstFrog(
      memeplexWorst,
      memeplexBest,
      taskCount,
      serviceCount,
      matrix
    );

    if (improved.fitness > memeplexWorst.fitness) {
      memeplex[memeplex.length - 1] = improved;
    } else {
      // If not improved, try using global best
      improved = updateWorstFrog(
        memeplexWorst,
        globalBest,
        taskCount,
        serviceCount,
        matrix
      );

      if (improved.fitness > memeplexWorst.fitness) {
        memeplex[memeplex.length - 1] = improved;
      } else {
        // If still not improved, replace with random solution
        const randomFrog: Chromosome = {
          x: Array(taskCount)
            .fill(0)
            .map(() => Math.floor(Math.random() * serviceCount)),
          fitness: 0,
          rwp: 0,
        };
        calculateChromosomeFitness(randomFrog, matrix, taskCount, serviceCount);
        memeplex[memeplex.length - 1] = randomFrog;
      }
    }
  }
}

/**
 * Runs SFLA for specified generations
 */
export async function runSFLA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Create memeplexes
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    // Find global best
    const globalBest = findBestChromosome(population);

    // Evolve each memeplex
    evolveSFLA(population, memeplexes, taskCount, serviceCount, matrix, globalBest);

    // Shuffle memeplexes (redistribute)
    shuffleMemeplexes(memeplexes, population);

    // Recreate memeplexes for next generation
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}

